import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Heading,
  Text,
  Button,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { FiSettings, FiEdit } from 'react-icons/fi';
import StudentsTab from './tabs/StudentsTab';
import CertificatesTab from './tabs/CertificatesTab';
import CriteriaTab from './tabs/CriteriaTab';
import { canvasAPI } from '../../services/api';
import { useLTI } from '../../contexts/LTIContext';

const ProfessorDashboard = () => {
  const navigate = useNavigate();
  const { isAdmin } = useLTI();
  const [students, setStudents] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [defaultDuration, setDefaultDuration] = useState('40 horas');
  const [contextTitle, setContextTitle] = useState('');
  const [settings, setSettings] = useState(null);
  const toast = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadStudents(),
        loadCertificates(),
        loadSettings(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const response = await canvasAPI.getCourseStudents();
      console.log('📥 Response from backend:', response);
      setStudents(response.students || []);
      // Guardar valores predeterminados del backend
      if (response.default_duration) {
        console.log('✅ Setting default_duration:', response.default_duration);
        setDefaultDuration(response.default_duration);
      }
      if (response.context_title) {
        console.log('✅ Setting context_title:', response.context_title);
        setContextTitle(response.context_title);
      }
    } catch (error) {
      console.error('Error loading students:', error);

      // Manejar error de OAuth2 específicamente
      if (error.response?.status === 401 && error.response?.data?.detail?.error === 'oauth_token_required') {
        toast({
          title: '🔐 Autorización OAuth2 Requerida',
          description: 'El token de Canvas ha expirado. Por favor, ve al Panel de Administración y autoriza OAuth2 nuevamente.',
          status: 'warning',
          duration: 10000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Error',
          description: 'No se pudo cargar la lista de estudiantes',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const loadCertificates = async () => {
    try {
      const response = await canvasAPI.getCourseCertificates();
      setCertificates(response.certificates || []);
    } catch (error) {
      console.error('Error loading certificates:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar los certificados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const loadSettings = async () => {
    try {
      const response = await canvasAPI.getInstructorSettings();
      setSettings(response.settings || null);
    } catch (error) {
      console.error('Error loading settings:', error);
      // No mostrar error al usuario, settings es opcional
    }
  };

  const handleSyncGrades = async () => {
    try {
      setSyncing(true);
      const response = await canvasAPI.syncGrades();

      toast({
        title: 'Calificaciones sincronizadas',
        description: `${response.synced || 0} calificaciones actualizadas`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      await loadStudents();
    } catch (error) {
      console.error('Error syncing grades:', error);
      toast({
        title: 'Error',
        description: 'No se pudo sincronizar las calificaciones',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleRefresh = async () => {
    await loadData();
  };

  return (
    <Box maxW="7xl" mx="auto" py={8} px={4}>
      {/* Header */}
      <Box mb={6}>
        <HStack justify="space-between" align="start">
          <Box>
            <Heading size="xl" mb={2}>
              Panel de Instructor
            </Heading>
            <Text color="gray.600">
              Gestiona estudiantes, calificaciones y emisión de certificados
            </Text>
          </Box>
          <Button
            leftIcon={<FiEdit />}
            colorScheme="purple"
            variant="outline"
            onClick={() => navigate('/canvas/signature')}
          >
            Mi Firma
          </Button>
        </HStack>
      </Box>

      {/* Tabs */}
      <Tabs colorScheme="blue" variant="enclosed" isLazy>
        <TabList>
          <Tab>Estudiantes</Tab>
          <Tab>Certificados Emitidos</Tab>
          <Tab>Criterios de Selección</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}>
            <StudentsTab
              students={students}
              loading={loading}
              syncing={syncing}
              onSyncGrades={handleSyncGrades}
              onRefresh={handleRefresh}
              onCertificatesCreated={loadData}
              defaultDuration={defaultDuration}
              contextTitle={contextTitle}
              settings={settings}
            />
          </TabPanel>

          <TabPanel px={0}>
            <CertificatesTab
              certificates={certificates}
              loading={loading}
              onRefresh={loadCertificates}
            />
          </TabPanel>

          <TabPanel px={0}>
            <CriteriaTab
              students={students}
              loading={loading}
              onEvaluate={loadStudents}
              onCertificatesCreated={loadData}
              defaultDuration={defaultDuration}
              contextTitle={contextTitle}
              settings={settings}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default ProfessorDashboard;
